import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Secrets that links put in an address (login token, payment return signature, reset token, the client's page a login
 * leads to…). The inline script at the top of public/index.html moves them out of the address before any analytics
 * script can send it, keeps them for the tab under the cleaned path, and the pages read them back here. Keep both
 * lists alike.
 */
export const ADDRESS_SECRETS = [
  "login_token",
  "user_identification",
  "token",
  "sig",
  "appointment",
  "user",
  "imp",
  "last_connection",
  "user_is_active",
  "user-identification",
  "redirect",
];

/** A token the shell hid from a path, such as the one of a signing link, is kept under this name. */
export const PATH_TOKEN = "path_token";
export const HIDDEN_SEGMENT = "_";

const STORAGE_KEY = "sum:address-secrets";

type Held = { path: string; secrets: Record<string, string> };

declare global {
  interface Window {
    __sumAddressSecrets?: Held;
  }
}

const read = (): Held | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Held;
  } catch {}
  return window.__sumAddressSecrets ?? null;
};

const write = (held: Held) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(held));
  } catch {
    window.__sumAddressSecrets = held;
  }
};

export const addressSecretsOf = (pathname: string): Record<string, string> => {
  const held = read();
  return held && held.path === pathname ? held.secrets : {};
};

/** The query of a page as its link gave it, the secrets moved out of its address included. */
export const searchWithSecrets = (
  pathname: string,
  search: string
): URLSearchParams => {
  const params = new URLSearchParams(search);
  Object.entries(addressSecretsOf(pathname)).forEach(([key, value]) => {
    if (!params.has(key)) params.set(key, value);
  });
  return params;
};

/** The real value of a path segment the shell hid. */
export const pathTokenOf = (
  pathname: string,
  segment: string | undefined
): string | undefined =>
  segment === HIDDEN_SEGMENT ? addressSecretsOf(pathname)[PATH_TOKEN] : segment;

/** Drops secrets a page has used once, so that a later visit of the same page does not use them again. */
export const forgetAddressSecrets = (pathname: string, keys: string[]) => {
  const held = read();
  if (!held || held.path !== pathname) return;
  const secrets = { ...held.secrets };
  keys.forEach((key) => delete secrets[key]);
  write({ path: held.path, secrets });
};

/** For a navigation inside the app: the address without its secrets, which wait for its page in the tab. */
export const withoutSecrets = (href: string): string => {
  const url = new URL(href, window.location.origin);
  const secrets: Record<string, string> = {};
  ADDRESS_SECRETS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value === null) return;
    secrets[key] = value;
    url.searchParams.delete(key);
  });
  if (Object.keys(secrets).length > 0) write({ path: url.pathname, secrets });
  return `${url.pathname}${url.search}${url.hash}`;
};

export const useSearchWithSecrets = (): URLSearchParams => {
  const { pathname, search } = useLocation();
  return useMemo(() => searchWithSecrets(pathname, search), [pathname, search]);
};

export const usePathToken = (segment: string | undefined) => {
  const { pathname } = useLocation();
  return pathTokenOf(pathname, segment);
};
